import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { nav, company } from "@/data/site";
import { Logo } from "@/components/Logo";
import { TerminDialog } from "@/components/TerminDialog";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    return () => (document.documentElement.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-400",
          scrolled && !open ? "bg-[rgba(8,8,10,0.85)] backdrop-blur-xl border-b hairline" : "bg-transparent border-b border-transparent"
        )}
      >
        <div className="container-x flex h-[68px] md:h-[76px] items-center justify-between">
          <Logo />
          <nav aria-label="Hauptnavigation" className="hidden md:flex items-center gap-8">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s|ü/g, (c) => (c === "ü" ? "ue" : "-"))}`}
                className={({ isActive }) => cn("link-underline text-[15px] transition-colors duration-300 text-[color:var(--ink-2)] hover:text-[color:var(--ink)]", isActive && "text-[color:var(--ink)]")}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <TerminDialog testId="nav-appointment-button" className="hidden sm:inline-flex btn-violet !py-2.5 !min-h-[44px]" />
            <button
              type="button"
              aria-label={open ? "Menü schliessen" : "Menü öffnen"}
              aria-expanded={open}
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="md:hidden relative h-11 w-11 -mr-2 flex flex-col items-center justify-center gap-[6px] text-[color:var(--ink)]"
            >
              <span className={cn("block h-[1.5px] w-6 bg-current transition-transform duration-400", open && "translate-y-[3.75px] rotate-45")} />
              <span className={cn("block h-[1.5px] w-6 bg-current transition-transform duration-400", open && "-translate-y-[3.75px] -rotate-45")} />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div data-testid="mobile-menu-overlay" className="fixed inset-0 z-40 bg-[color:var(--bg)] flex flex-col">
          <div className="container-x flex-1 flex flex-col justify-center pt-20 pb-10">
            <nav aria-label="Mobile Navigation" className="flex flex-col">
              {[{ label: "Startseite", to: "/" }, ...nav].map((n, i) => (
                <Link key={n.to} to={n.to} data-testid={`mobile-nav-link-${i}`} className="reveal-up block py-4 border-b hairline text-[30px] font-semibold tracking-[-0.02em] leading-none text-[color:var(--ink)]" style={{ animationDelay: `${0.03 + i * 0.04}s` }}>{n.label}</Link>
              ))}
            </nav>
            <div className="reveal-up mt-8 space-y-6" style={{ animationDelay: "0.2s" }}>
              <TerminDialog testId="mobile-nav-appointment" className="btn-violet w-full" />
              <div className="grid grid-cols-2 gap-6 text-sm text-[color:var(--ink-2)]">
                <div>
                  <p className="eyebrow mb-2">Kontakt</p>
                  <a href={company.phoneHref} className="block text-[color:var(--ink)]">{company.phone}</a>
                  {company.confirmed.email && <a href={`mailto:${company.email}`} className="block">{company.email}</a>}
                </div>
                <div>
                  <p className="eyebrow mb-2">Standort</p>
                  {company.confirmed.address ? (
                    <>
                      <p className="text-[color:var(--ink)]">{company.street}</p>
                      <p>{company.zip} {company.city}</p>
                    </>
                  ) : (
                    <p className="text-[color:var(--ink)]">{company.region}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
